import {
  getUserModule,
  hasOneOfPermissions,
  hasPermissions,
  Module,
  UserPermission,
} from '@repo/permissions'
import { useCallback, useMemo } from 'react'
import { useMe } from '../queries/useMe'

export type RouteStaticDataPermissions = {
  options: { staticData?: { permissions?: UserPermission[] } }
  parentRoute?: RouteStaticDataPermissions
}

export const hasRoutePermissions = (
  permissions: UserPermission[],
  route: RouteStaticDataPermissions,
) => {
  if (!route.options?.staticData?.permissions) return true

  return hasPermissions(permissions, ...route.options.staticData.permissions)
}

export const hasRouteOneOfPermissions = (
  permissions: UserPermission[],
  route: RouteStaticDataPermissions,
) => {
  if (!route.options?.staticData?.permissions) return true

  return hasOneOfPermissions(permissions, ...route.options.staticData.permissions)
}

const usePermissions = () => {
  const { data: me } = useMe()
  const permissions: UserPermission[] = useMemo(() => me?.permissions || [], [me])

  const userModule: Module = getUserModule((me?.roles || []).map((r) => r.name))

  const verifyRoutePermissions = useCallback(
    (route: RouteStaticDataPermissions) => hasRoutePermissions(permissions, route),
    [permissions],
  )

  const verifyRouteAtLeastOnePermission = useCallback(
    (route: RouteStaticDataPermissions) => hasRouteOneOfPermissions(permissions, route),
    [permissions],
  )

  const canUser = useCallback(
    (...requiredPermissions: UserPermission[]) =>
      hasPermissions(permissions, ...requiredPermissions),
    [permissions],
  )

  return {
    permissions,
    userModule,
    verifyRoutePermissions,
    verifyRouteAtLeastOnePermission,
    canUser,
  }
}

export default usePermissions
